/**
 * 신고이력 정보공개 동의(신고비율 수준) — 프런트 공용 (63차, 2026-09-16).
 *  - 문안 사본: 가입 화면은 로그인 전이라 서버의 `consent_text`를 못 받으므로 같은 버전의 사본을 둔다.
 *    서버는 `version`만 대조한다(문안이 바뀌면 서버 버전이 올라가고 구버전 요청은 409 → 화면이 다시 묻는다).
 *  - 가입 때 체크한 동의는 토큰이 없어 바로 적재할 수 없다 → localStorage 에 보류하고 첫 로그인 뒤 인증된 호출로 적재.
 * 설계: docs/superpowers/specs/2026-09-16-disclosure-consent-company-account-design.md §5·§6
 */
export const CONSENT_LEVEL = "distribution";
export const CONSENT_VERSION = "disclosure-distribution-v1";
export const CONSENT_TITLE = "신고이력 정보공개 동의(신고비율 수준)";
export const CONSENT_LINES = [
  "동의 대상: 당사(사업자번호 기준)가 신고인 또는 화주로 관여한 수출입 신고이력.",
  "공개 범위: 세번(HS)별 신고비율·건수·기간과 그로부터 만든 익명 통계에 한합니다. 개별 신고 건, 거래처, 규격·성분, 수량·단가·금액, 신고번호는 공개하지 않습니다.",
  "이용 목적: loginHS 이용자가 품목분류를 검토할 때 관행 참고자료로 제공합니다. 법적 확정이나 세관 판단을 대신하지 않습니다.",
  "제공 형태: 당사를 식별할 수 없는 집계값입니다. 표본이 적어 식별 우려가 있으면 표시하지 않습니다.",
  "보유·이용 기간: 동의 철회 시까지. 철회하면 이후 집계에서 제외됩니다(이미 배포된 통계는 재계산 시 반영).",
  "철회: 회사 계정 화면 또는 고객센터에서 언제든 철회할 수 있으며 철회 이력이 남습니다.",
  "동의하지 않아도 loginHS 이용에 제한은 없습니다.",
];

export const PENDING_KEY = "loginhs:pending-disclosure-consent";

const storage = () => { try { return window.localStorage; } catch (_) { return null; } };

/** 가입 화면에서 체크한 동의를 보류한다(로그인 뒤 적재). */
export function savePendingConsent({ businessNumber, agreed = true }) {
  const s = storage(); if (!s) return null;
  const rec = { businessNumber: String(businessNumber || "").replace(/\D/g, ""), agreed: Boolean(agreed), version: CONSENT_VERSION, agreedAt: new Date().toISOString() };
  try { s.setItem(PENDING_KEY, JSON.stringify(rec)); } catch (_) { return null; }
  return rec;
}

export function readPendingConsent() {
  const s = storage(); if (!s) return null;
  try {
    const raw = s.getItem(PENDING_KEY); if (!raw) return null;
    const rec = JSON.parse(raw);
    return rec && rec.version && typeof rec.agreed === "boolean" ? rec : null;
  } catch (_) { return null; }
}

export function clearPendingConsent() {
  const s = storage(); if (!s) return;
  try { s.removeItem(PENDING_KEY); } catch (_) { /* ignore */ }
}

/**
 * 로그인 직후 — 보류된 가입 동의를 적재한다. 결과: "submitted" | "none" | "skipped" | "failed".
 *  - 회사 가입자가 아니면(403) 보류를 지운다(개인 가입자로 가입한 경우).
 *  - 문안이 바뀌어 409면 보류를 지우고 화면이 다시 묻게 둔다.
 *  - 네트워크 실패는 보류를 남긴다(다음 로그인에 재시도).
 */
export async function submitPendingDisclosureConsent(token, { fetchImpl = (typeof fetch === "function" ? fetch : null), apiBase = "" } = {}) {
  const rec = readPendingConsent();
  if (!rec) return "none";
  if (!token || !fetchImpl) return "skipped";
  try {
    const r = await fetchImpl(`${apiBase}/api/onto/company/consent`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ agreed: rec.agreed, version: rec.version, channel: "signup" }),
    });
    if (r.ok) { clearPendingConsent(); return "submitted"; }
    if (r.status === 403 || r.status === 409) { clearPendingConsent(); return "skipped"; }
    return "failed";
  } catch (_) {
    return "failed";
  }
}

const authHeaders = (token) => ({ "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) });

export async function fetchCompanyMe(token, { fetchImpl = fetch, apiBase = "" } = {}) {
  const r = await fetchImpl(`${apiBase}/api/onto/company/me`, { headers: authHeaders(token) });
  if (!r.ok) throw Object.assign(new Error(`HTTP ${r.status}`), { status: r.status });
  return r.json();
}

export async function postConsent(token, agreed, version, { fetchImpl = fetch, apiBase = "" } = {}) {
  const r = await fetchImpl(`${apiBase}/api/onto/company/consent`, {
    method: "POST", headers: authHeaders(token), body: JSON.stringify({ agreed, version: version || CONSENT_VERSION, channel: "web" }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw Object.assign(new Error(d.message || `HTTP ${r.status}`), { status: r.status });
  return d;
}

export async function postCompanyRequest(token, body, { fetchImpl = fetch, apiBase = "" } = {}) {
  const r = await fetchImpl(`${apiBase}/api/onto/company/request`, { method: "POST", headers: authHeaders(token), body: JSON.stringify(body) });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw Object.assign(new Error(d.message || `HTTP ${r.status}`), { status: r.status });
  return d;
}

/** 66차: 사업자번호 → 업체 정보(관세청 통관고유부호 + 국세청). 사용자 토큰·admin_token 모두 가능. */
export async function lookupBusiness(token, bizNo, { fetchImpl = fetch, apiBase = "" } = {}) {
  const d = String(bizNo || "").replace(/\D/g, "");
  if (d.length !== 10) throw Object.assign(new Error("사업자번호는 숫자 10자리여야 합니다."), { status: 400 });
  const r = await fetchImpl(`${apiBase}/api/onto/company/biz-lookup?biz_no=${d}`, { headers: authHeaders(token) });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw Object.assign(new Error(j.message || `HTTP ${r.status}`), { status: r.status });
  return j; // { found, info{corp_name, corp_name_en, ceo_name, corp_addr, tel, email, customs_office, nts_status, biz_type, biz_item} }
}

/**
 * 71차: 가입 화면(로그인 전)용 공개 조회 — 토큰이 없다. 서버가 IP 한 곳당 10분 20회로 막는다(초과 429).
 * 응답 형태는 인증 경로와 같다.
 */
/** 사업자 회원 구분 — 회사 표 role 값과 화면 이름. */
export const MEMBER_ROLES = [
  { id: "shipper", label: "화주(수출입 기업)" },
  { id: "broker", label: "관세사" },
  { id: "both", label: "둘 다" },
];

/**
 * 72차 보강 3: 사업자 회원가입 직후 회사 정보를 회사 표에 등록한다(로그인 전).
 * 서버가 방금 가입한 계정·같은 사업자번호일 때만 받고, 늘 검토 대기로 만든다. 실패는 가입을 막지 않는다.
 */
export async function registerCompanyAtSignup(payload, { fetchImpl = fetch, apiBase = "" } = {}) {
  const r = await fetchImpl(`${apiBase}/api/onto/company/signup-register`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload || {}),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw Object.assign(new Error(j.message || `HTTP ${r.status}`), { status: r.status });
  return j;
}

export async function lookupBusinessPublic(bizNo, { fetchImpl = fetch, apiBase = "" } = {}) {
  const d = String(bizNo || "").replace(/\D/g, "");
  if (d.length !== 10) throw Object.assign(new Error("사업자번호는 숫자 10자리여야 합니다."), { status: 400 });
  const r = await fetchImpl(`${apiBase}/api/onto/company/biz-lookup/public?biz_no=${d}`);
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw Object.assign(new Error(j.message || `HTTP ${r.status}`), { status: r.status });
  return j;
}

/** 조회 결과를 폼 필드로(비어 있지 않은 값만 덮어쓴다). */
export function fillFromLookup(form, info) {
  if (!info) return form;
  const pick = (k, v) => (v != null && String(v).trim() !== "" ? { [k]: String(v).trim() } : {});
  return {
    ...form,
    ...pick("corp_name", info.corp_name), ...pick("corp_name_en", info.corp_name_en), ...pick("ceo_name", info.ceo_name),
    ...pick("corp_addr", info.corp_addr), ...pick("contact_phone", info.tel), ...pick("contact_email", info.email),
    ...pick("customs_office", info.customs_office), ...pick("nts_status", info.nts_status),
    ...pick("biz_type", info.biz_type), ...pick("biz_item", info.biz_item),
  };
}

export const STATUS_LABEL = { pending: "확인 대기", verified: "확인 완료", rejected: "반려", suspended: "중지" };
export const ROLE_LABEL = { broker: "관세사(신고인)", shipper: "화주(수입·수출자)", both: "관세사·화주" };

export function formatBusinessNo(d) {
  const s = String(d || "").replace(/\D/g, "");
  return s.length === 10 ? `${s.slice(0, 3)}-${s.slice(3, 5)}-${s.slice(5)}` : s;
}
